/**
 * Activity Handlers (Actividad tab)
 *
 * Pure command handlers — no side effects, no API calls, no dispatch.
 * One handler per command type.
 */

import { CommandHandlerMap } from '@commandDrivenStateEngine/core/types';
import { ActivityState } from './state';
import { ActivityCommand } from './commands';

type ExtractCommand<T extends string> = Extract<ActivityCommand, { type: T }>;

// -------- Data handlers ----------

const handleSetExecutions = (
  state: ActivityState,
  cmd: ExtractCommand<'automation/activity/data/setExecutions'>
): ActivityState => ({
  ...state,
  data: {
    ...state.data,
    executions: cmd.payload.executions
  }
});

const handleSetStatusCounts = (
  state: ActivityState,
  cmd: ExtractCommand<'automation/activity/data/setStatusCounts'>
): ActivityState => ({
  ...state,
  data: {
    ...state.data,
    statusCounts: cmd.payload.statusCounts
  }
});

const handleSetTrace = (
  state: ActivityState,
  cmd: ExtractCommand<'automation/activity/data/setTrace'>
): ActivityState => ({
  ...state,
  data: {
    ...state.data,
    traceExecution: cmd.payload.execution
  }
});

const handleSetTraceRuleDefinition = (
  state: ActivityState,
  cmd: ExtractCommand<'automation/activity/data/setTraceRuleDefinition'>
): ActivityState => ({
  ...state,
  data: {
    ...state.data,
    traceRuleDefinition: cmd.payload.ruleDefinition
  }
});

// -------- UI handlers ----------

const handleSetExecutionFilter = (
  state: ActivityState,
  cmd: ExtractCommand<'automation/activity/ui/setExecutionFilter'>
): ActivityState => ({
  ...state,
  ui: {
    ...state.ui,
    executionFilter: cmd.payload.filter,
    page: 1
  }
});

const handleSetTriggeringEventFilter = (
  state: ActivityState,
  cmd: ExtractCommand<'automation/activity/ui/setTriggeringEventFilter'>
): ActivityState => ({
  ...state,
  ui: {
    ...state.ui,
    triggeringEventNameFilter: cmd.payload.triggeringEventName,
    page: 1
  }
});

const handleSetBatchFilter = (
  state: ActivityState,
  cmd: ExtractCommand<'automation/activity/ui/setBatchFilter'>
): ActivityState => ({
  ...state,
  ui: {
    ...state.ui,
    batchIdFilter: cmd.payload.batchId,
    page: 1
  }
});

const handleSetHierarchyLevelFilter = (
  state: ActivityState,
  cmd: ExtractCommand<'automation/activity/ui/setHierarchyLevelFilter'>
): ActivityState => ({
  ...state,
  ui: {
    ...state.ui,
    hierarchyLevelFilter: cmd.payload.hierarchyLevel,
    page: 1
  }
});

const handleSetEntityFilter = (
  state: ActivityState,
  cmd: ExtractCommand<'automation/activity/ui/setEntityFilter'>
): ActivityState => ({
  ...state,
  ui: {
    ...state.ui,
    targetEntityIdFilter: cmd.payload.targetEntityId,
    page: 1
  }
});

const handleSetPage = (
  state: ActivityState,
  cmd: ExtractCommand<'automation/activity/ui/setPage'>
): ActivityState => ({
  ...state,
  ui: {
    ...state.ui,
    page: cmd.payload.page
  }
});

const handleOpenTrace = (
  state: ActivityState,
  cmd: ExtractCommand<'automation/activity/ui/openTrace'>
): ActivityState => ({
  ...state,
  data: {
    ...state.data,
    traceExecution: null,
    traceRuleDefinition: null
  },
  ui: {
    ...state.ui,
    traceExecutionId: cmd.payload.executionId
  },
  loading: {
    ...state.loading,
    traceLoading: true
  }
});

const handleCloseTrace = (
  state: ActivityState,
  _cmd: ExtractCommand<'automation/activity/ui/closeTrace'>
): ActivityState => ({
  ...state,
  data: {
    ...state.data,
    traceExecution: null,
    traceRuleDefinition: null
  },
  ui: {
    ...state.ui,
    traceExecutionId: null
  }
});

// -------- Loading handlers ----------

const handleSetExecutionsLoading = (
  state: ActivityState,
  cmd: ExtractCommand<'automation/activity/loading/setExecutionsLoading'>
): ActivityState => ({
  ...state,
  loading: {
    ...state.loading,
    executionsLoading: cmd.payload.isLoading
  }
});

const handleSetTraceLoading = (
  state: ActivityState,
  cmd: ExtractCommand<'automation/activity/loading/setTraceLoading'>
): ActivityState => ({
  ...state,
  loading: {
    ...state.loading,
    traceLoading: cmd.payload.isLoading
  }
});

const handleSetRetrying = (
  state: ActivityState,
  cmd: ExtractCommand<'automation/activity/loading/setRetrying'>
): ActivityState => ({
  ...state,
  loading: {
    ...state.loading,
    retrying: {
      ...state.loading.retrying,
      [cmd.payload.executionId]: cmd.payload.isRetrying
    }
  }
});

// -------- Handler map ----------

export const handlers: CommandHandlerMap<ActivityState, ActivityCommand> = {
  // Data
  'automation/activity/data/setExecutions': handleSetExecutions,
  'automation/activity/data/setStatusCounts': handleSetStatusCounts,
  'automation/activity/data/setTrace': handleSetTrace,
  'automation/activity/data/setTraceRuleDefinition': handleSetTraceRuleDefinition,
  // UI
  'automation/activity/ui/setExecutionFilter': handleSetExecutionFilter,
  'automation/activity/ui/setTriggeringEventFilter': handleSetTriggeringEventFilter,
  'automation/activity/ui/setBatchFilter': handleSetBatchFilter,
  'automation/activity/ui/setHierarchyLevelFilter': handleSetHierarchyLevelFilter,
  'automation/activity/ui/setEntityFilter': handleSetEntityFilter,
  'automation/activity/ui/setPage': handleSetPage,
  'automation/activity/ui/openTrace': handleOpenTrace,
  'automation/activity/ui/closeTrace': handleCloseTrace,
  // Loading
  'automation/activity/loading/setExecutionsLoading': handleSetExecutionsLoading,
  'automation/activity/loading/setTraceLoading': handleSetTraceLoading,
  'automation/activity/loading/setRetrying': handleSetRetrying
};
